import { InfoIcon } from 'lucide-react'

import { type PlanType } from 'components/plan_selection_preference/type'
import { Badge } from 'slab'
import { capitalize, cn } from 'utils'
import {
  type MemberOnboardingRenewalDentalPlan,
  type MemberOnboardingRenewalMedicalPlan,
  type MemberOnboardingRenewalVisionPlan,
} from 'utils/web_api.schemas'

import {
  type MetalTier,
  MetalTierBadge,
} from '../MetalTierBadge/MetalTierBadge'
import { PlanBadge } from '../PlanBadge/PlanBadge'

interface RenewalsPlanComparisonMedicalHeaderProps {
  plan?: MemberOnboardingRenewalMedicalPlan | null;
  planType: 'medical';
}
interface RenewalsPlanComparisonDentalHeaderProps {
  plan?: MemberOnboardingRenewalDentalPlan | null;
  planType: 'dental';
}
interface RenewalsPlanComparisonVisionHeaderProps {
  plan?: MemberOnboardingRenewalVisionPlan | null;
  planType: 'vision';
}
type RenewalsPlanComparisonHeaderProps =
  | RenewalsPlanComparisonMedicalHeaderProps
  | RenewalsPlanComparisonDentalHeaderProps
  | RenewalsPlanComparisonVisionHeaderProps

const RenewalsPlanComparisonHeader: React.FC<
  RenewalsPlanComparisonHeaderProps
> = ({ plan, planType }) => {
  let planName = ''

  if (plan) {
    // We use carrier name + plan name (friendly name from the backend
    // friendly name already attempts to strip out the carrier name) for medical plans
    // and carrier name + plan code for dental and vision plans
    // We also show metal tier and plan type only for medical plans
    if (planType === 'medical') {
      planName = plan.carrier_name + ' ' + plan.plan_name
    } else {
      const planToLookup = plan as
        | MemberOnboardingRenewalDentalPlan
        | MemberOnboardingRenewalVisionPlan
      planName = planToLookup.carrier_name + ' ' + capitalize(planToLookup.plan_code)
    }
  }

  return (
    <div className='flex gap-x-4'>
      {plan?.carrier_image_url && plan?.carrier_image_url.length > 0
        ? (
          <div className='h-12 w-12 shrink-0 rounded-full p-2 border border-gray-200 flex items-center justify-center'>
            <img
              aria-hidden
              src={plan.carrier_image_url}
              className='block object-contain h-full w-full'
              alt='Carrier logo'
            />
          </div>
          )
        : (
          <div className='h-12 w-12 shrink-0 rounded-full p-2 border border-dashed border-gray-200 flex items-center justify-center' />
          )}
      <div className={cn('flex flex-col', planType !== 'medical' && 'gap-1')}>
        <h1 className='heading-sm text-gray-800'>
          {capitalize(planType)} plan
        </h1>
        <div className='flex flex-row gap-2 justify-start items-center'>
          {!plan && (
            <Badge leftIcon={InfoIcon} type='neutral'>
              New plan needed
            </Badge>
          )}
          {plan && <div className='body-sm-bold text-gray-800'>{planName}</div>}
          {planType === 'medical' && plan && (
            <>
              {plan.metal_tier && (
                <MetalTierBadge
                  bordered
                  tier={plan.metal_tier as MetalTier}
                />
              )}
              {plan.plan_type && (
                <PlanBadge
                  bordered
                  type={plan.plan_type as PlanType}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default RenewalsPlanComparisonHeader
